import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  Check,
  Wallet,
  Building2,
  Mail,
  User,
  ShieldCheck,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/input";
import { useAuth } from "../../auth/AuthContext";
import { getStoredPaymentAccount } from "../../lib/paymentsStorage";
import {
  getPaymentSetupInitialValues,
  setPaymentSetupFormDraft,
  type PaymentSetupFormDraft,
} from "../../lib/paymentSetupFormStorage";
import { createPaymentAccount, getCreatePaymentAccountErrorMessage } from "../../services/paymentsApi";
import { BetaBadge } from "../../components/ui/BetaBadge";
import { clearSettingsCache } from "../../services/settings";

function Field({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <Icon className="h-4 w-4 text-[#885ABB]" aria-hidden />
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function openXonderLinkInNewTab(url: string): boolean {
  const win = window.open(url, "_blank", "noopener,noreferrer");
  return win != null;
}

const SETUP_STEPS = [
  "Enter your contact and business details below.",
  "We create your Xonder merchant account and open setup in a new tab.",
  "Complete verification on Xonder, then return here to receive payments.",
] as const;

const LINK_NEXT_STEPS = [
  "Open the Xonder setup link (a new tab may already be open).",
  "Complete identity and business verification on Xonder.",
  "Return to Glossa — payments will appear once setup is finished.",
] as const;

type AccountLinkSuccessViewProps = {
  accountLinkUrl: string;
  businessName: string;
  copied: boolean;
  onCopied: () => void;
  onCopyError: () => void;
  onBack: () => void;
};

function AccountLinkSuccessView({
  accountLinkUrl,
  businessName,
  copied,
  onCopied,
  onCopyError,
  onBack,
}: AccountLinkSuccessViewProps) {
  return (
    <div className="mx-auto w-full max-w-xl">
      <div className="rounded-2xl border border-emerald-200/80 bg-white shadow-lg shadow-emerald-100/40 overflow-hidden">
        <div className="bg-gradient-to-br from-emerald-50 to-white px-6 py-8 sm:px-8 sm:py-10 text-center border-b border-emerald-100">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 ring-4 ring-emerald-50">
            <CheckCircle2 className="h-9 w-9" strokeWidth={2} />
          </div>
          <h2 className="mt-5 text-xl font-bold text-gray-900 sm:text-2xl">Account created</h2>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            {businessName ? (
              <>
                Your Xonder merchant account for <strong className="text-gray-800">{businessName}</strong>{" "}
                is ready. Use the link below to finish onboarding.
              </>
            ) : (
              <>Your Xonder merchant account is ready. Use the link below to finish onboarding.</>
            )}
          </p>
        </div>

        <div className="px-6 py-6 sm:px-8 sm:py-7 space-y-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
              Xonder setup link
            </p>
            <div className="rounded-xl border-2 border-dashed border-[#885ABB]/30 bg-purple-50/40 p-4">
              <Input
                readOnly
                value={accountLinkUrl}
                className="h-12 border-gray-200 bg-white text-sm text-gray-800 shadow-sm"
                onFocus={(e) => e.target.select()}
              />
              <p className="mt-3 text-xs text-gray-500 text-center">
                This link is unique to your account. Do not share it publicly.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              className="w-full sm:flex-1 h-12 bg-[#885ABB] hover:bg-[#6f47a2] text-white gap-2 text-base"
              onClick={() => openXonderLinkInNewTab(accountLinkUrl)}
            >
              <ExternalLink className="h-5 w-5" />
              Open in new tab
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full sm:flex-1 h-12 border-gray-200 gap-2"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(accountLinkUrl);
                  onCopied();
                } catch {
                  onCopyError();
                }
              }}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>

          <div className="rounded-lg bg-gray-50 border border-gray-100 p-4">
            <p className="text-sm font-medium text-gray-800 mb-3">What to do next</p>
            <ol className="space-y-3">
              {LINK_NEXT_STEPS.map((step, i) => (
                <li key={step} className="flex gap-3 text-sm text-gray-600 leading-relaxed">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white border border-gray-200 text-xs font-bold text-[#885ABB]">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="border-t border-gray-100 px-6 py-4 sm:px-8 bg-gray-50/80 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:flex-1 border-gray-200"
            onClick={onBack}
          >
            Back to payments
          </Button>
        </div>
      </div>
    </div>
  );
}

const PaymentSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser, syncSettings, syncSettingsToLocalStorage } = useAuth();
  const [fullName, setFullName] = useState(
    () => getPaymentSetupInitialValues(null, getStoredPaymentAccount()).fullName
  );
  const [email, setEmail] = useState(
    () => getPaymentSetupInitialValues(null, getStoredPaymentAccount()).email
  );
  const [businessName, setBusinessName] = useState(
    () => getPaymentSetupInitialValues(null, getStoredPaymentAccount()).businessName
  );
  const [submitting, setSubmitting] = useState(false);
  const [accountLinkUrl, setAccountLinkUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const u = user as Record<string, unknown>;
    const company =
      u.company && typeof u.company === "object"
        ? (u.company as Record<string, unknown>)
        : null;
    const n = typeof u.name === "string" ? u.name.trim() : "";
    const em = typeof u.email === "string" ? u.email.trim() : "";
    const biz =
      (typeof company?.company_name === "string" && company.company_name.trim()) ||
      (typeof company?.name === "string" && company.name.trim()) ||
      "";

    setFullName((f) => f.trim() || n || f);
    setEmail((e) => e.trim() || em || e);
    setBusinessName((b) => b.trim() || biz || b);
  }, [user]);

  const persistDraft = useCallback(() => {
    const draft: PaymentSetupFormDraft = {
      fullName: fullName.trim(),
      email: email.trim(),
      businessName: businessName.trim(),
    };
    setPaymentSetupFormDraft(draft);
  }, [fullName, email, businessName]);

  useEffect(() => {
    const t = window.setTimeout(persistDraft, 500);
    return () => window.clearTimeout(t);
  }, [persistDraft]);

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    const draft: PaymentSetupFormDraft = {
      fullName: fullName.trim(),
      email: email.trim(),
      businessName: businessName.trim(),
    };

    if (!draft.fullName || !draft.email || !draft.businessName) {
      toast.error("Please fill in full name, email, and business name.");
      return;
    }

    setPaymentSetupFormDraft(draft);
    setSubmitting(true);
    setAccountLinkUrl(null);
    setCopied(false);

    try {
      const companyId =
        user &&
        typeof user === "object" &&
        user !== null &&
        "company" in user &&
        (user as { company?: { id?: unknown } }).company &&
        typeof (user as { company: { id?: unknown } }).company === "object"
          ? (user as { company: { id?: unknown } }).company.id
          : undefined;

      const { url, accountId } = await createPaymentAccount({
        name: draft.fullName,
        email: draft.email,
        business_name: draft.businessName,
        ...(companyId != null && companyId !== ""
          ? { company_id: typeof companyId === "number" ? companyId : String(companyId) }
          : {}),
      });

      if (accountId && user?.company) {
        updateUser({
          company: {
            ...user.company,
            stripe_id: accountId,
          },
        });
      }

      try {
        clearSettingsCache();
        await syncSettingsToLocalStorage();
        await syncSettings();
      } catch (syncErr) {
        console.error("Failed to refresh account after payment setup:", syncErr);
        toast.warn(
          "Account created, but profile sync failed. Refresh the page if payment status looks incorrect."
        );
      }

      setAccountLinkUrl(url);
      if (!openXonderLinkInNewTab(url)) {
        toast.warn("Allow pop-ups to open Xonder automatically, or use Open in new tab below.");
      } else {
        toast.success("Account created. Xonder setup opened in a new tab.");
      }
    } catch (err: unknown) {
      toast.error(getCreatePaymentAccountErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const showLinkView = Boolean(accountLinkUrl);

  return (
    <div className="fixed inset-0 z-[150] flex flex-col bg-gradient-to-b from-[#f8f5fc] via-gray-50 to-gray-100 overflow-hidden">
      <header className="shrink-0 border-b border-gray-200/80 bg-white/90 backdrop-blur-sm shadow-sm">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-8">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 text-gray-600 hover:bg-purple-50"
            onClick={() => navigate("/payments")}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to payments</span>
          </Button>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8 sm:py-10">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-6 mb-8">
            <div
              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg ${
                showLinkView
                  ? "bg-emerald-500 shadow-emerald-200/60"
                  : "bg-[#885ABB] shadow-purple-200/60"
              }`}
            >
              {showLinkView ? (
                <CheckCircle2 className="h-7 w-7" />
              ) : (
                <Wallet className="h-7 w-7" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-2 ${
                  showLinkView ? "text-emerald-700" : "text-[#885ABB]"
                }`}
              >
                Xonder payments
                <BetaBadge />
              </p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900 sm:text-3xl">
                {showLinkView ? "Complete Xonder setup" : "Set up your payment account"}
              </h1>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed max-w-xl">
                {showLinkView
                  ? "Your merchant account was created. Open the link below to finish verification on Xonder."
                  : "Connect Xonder so you can receive customer payments in Glossa. This only takes a few minutes."}
              </p>
            </div>
          </div>

          {showLinkView && accountLinkUrl ? (
            <AccountLinkSuccessView
              accountLinkUrl={accountLinkUrl}
              businessName={businessName.trim()}
              copied={copied}
              onCopied={() => {
                setCopied(true);
                toast.success("Link copied.");
                window.setTimeout(() => setCopied(false), 2000);
              }}
              onCopyError={() => toast.error("Could not copy link.")}
              onBack={() => navigate("/payments")}
            />
          ) : (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-start">
              <aside className="rounded-xl border border-purple-100 bg-white/80 p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#885ABB]" />
                  How it works
                </h2>
                <ol className="mt-4 space-y-4">
                  {SETUP_STEPS.map((step, i) => (
                    <li key={step} className="flex gap-3 text-sm text-gray-600 leading-relaxed">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-[#885ABB]">
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-5 text-xs text-gray-500 border-t border-gray-100 pt-4">
                  Your details are sent securely to create your Xonder merchant profile.
                </p>
              </aside>

              <form
                onSubmit={handleCreateAccount}
                className="rounded-xl border border-gray-200 bg-white shadow-sm"
              >
                <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
                  <h2 className="text-base font-semibold text-gray-900">Business details</h2>
                  <p className="mt-0.5 text-sm text-gray-500">
                    Pre-filled from your Glossa profile where available.
                  </p>
                </div>

                <div className="space-y-5 px-5 py-5 sm:px-6 sm:py-6">
                  <Field label="Full name" icon={User}>
                    <Input
                      className="h-11 border-gray-200 focus-visible:ring-[#885ABB]"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      autoComplete="name"
                      placeholder="e.g. Jane Smith"
                      required
                      disabled={submitting}
                    />
                  </Field>
                  <Field label="Email" icon={Mail}>
                    <Input
                      className="h-11 border-gray-200 focus-visible:ring-[#885ABB]"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      placeholder="you@yourbusiness.com"
                      required
                      disabled={submitting}
                    />
                  </Field>
                  <Field label="Business name" icon={Building2}>
                    <Input
                      className="h-11 border-gray-200 focus-visible:ring-[#885ABB]"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      autoComplete="organization"
                      placeholder="Your salon or company name"
                      required
                      disabled={submitting}
                    />
                  </Field>
                </div>

                <div className="border-t border-gray-100 px-5 py-4 sm:px-6 bg-gray-50/50 rounded-b-xl">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 bg-[#885ABB] hover:bg-[#6f47a2] text-white gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating Xonder account…
                      </>
                    ) : (
                      "Create account & open Xonder setup"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSetupPage;
